/**
 * LA FICHA UNICA DE LA PERSONA (SUJ-T8 bloque 1, va de Josse #17380).
 *
 * Josse: "me es muy dificil tener que ir a 3 vistas". Una sola ficha, que se abre igual desde
 * Personas, Colaboradores y Usuarios. Cada lista la abre con el id que ya tiene (persona,
 * ficha de colaborador o usuario) y la ficha hace UNA consulta: el GET de persona por esa via.
 * Todo lo de arriba (cabecera y "como va este mes") sale de esa respuesta; el detalle por
 * negocio se pide solo al abrirlo.
 *
 * NEXOS DE UNA SOLA FUENTE: persona.nexos (suma de nxs_transactions de sus llaves). Antes
 * Personas decia 267.367 y Usuarios decia 0: la ficha solo lee esta cifra.
 *
 * LO QUE NUNCA SE MUESTRA: el PIN ni su hash. Solo si lo tiene.
 *
 * Sin DOM: lo prueba scripts/check-ficha-unica.mjs.
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** Las tres puertas de entrada, con el id que cada lista ya tiene. */
export const PREFIJOS = { colaborador: '#/persona/colaborador/', usuario: '#/persona/usuario/', persona: '#/persona/' };

export function rutaDeLaFicha(tipo, id) {
  const pref = PREFIJOS[tipo];
  const limpio = texto(id);
  return pref && limpio ? pref + encodeURIComponent(limpio) : '';
}

/** Lee la ruta actual: { tipo, id } o null si no es una ficha. */
export function leerRutaDeLaFicha(hash) {
  const h = texto(hash);
  for (const tipo of ['colaborador', 'usuario', 'persona']) {
    const pref = PREFIJOS[tipo];
    if (!h.startsWith(pref)) continue;
    const resto = h.slice(pref.length);
    if (!resto || resto.includes('/')) continue;
    return { tipo, id: decodeURIComponent(resto) };
  }
  return null;
}

const pesosCOP = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('es-CO');
const miles = (n) => Math.trunc(Number(n) || 0).toLocaleString('es-CO');

/** Fecha y hora en Bogota, corta. Vacia si no hay fecha valida. */
export function cuando(iso) {
  const t = Date.parse(texto(iso));
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleString('es-CO', { timeZone: 'America/Bogota', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

/** La cabecera: iniciales, nombre y chips (negocios con cargo, llaves, PIN si/no, estado). */
export function cabeceraDeLaPersona(p) {
  const nombre = p?.nombre || 'sin nombre';
  const iniciales = nombre.split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0].toUpperCase()).join('') || '?';
  const chips = [];
  for (const m of p?.membresias || []) {
    chips.push({ texto: [m.negocio || 'Nexo', m.cargo || m.rol].filter(Boolean).join(' · '), ok: true });
  }
  chips.push(p?.llavePanel?.tiene ? { texto: 'Entra al panel', ok: true } : { texto: 'No entra al panel', ok: false });
  const ops = p?.llavesOperacion || [];
  if (ops.length) {
    const conPin = ops.filter((o) => o.tienePin).length;
    chips.push({ texto: ops.length === 1 ? `PIN: ${conPin ? 'sí' : 'no'}` : `PIN en ${conPin} de ${ops.length} fichas`, ok: conPin === ops.length });
  }
  const activa = !p?.estado || /^activ/i.test(p.estado);
  chips.push({ texto: activa ? 'Activa' : 'Inactiva', ok: activa });
  return { iniciales, nombre, chips };
}

/**
 * Las tarjetas de "como va este mes", todas de la MISMA respuesta del GET de persona.
 * Cada una dice su fuente; la que no tiene dato va apagada y dice que falta.
 */
export function tarjetasDelMes(p) {
  const negocio = (id) => (p?.membresias || []).find((m) => m.businessId === id)?.negocio || 'su negocio';
  const asis = p?.asistencia || {};
  const nexos = p?.nexos || {};
  const danos = p?.danos || [];
  const con = p?.conexiones || {};

  const tarjetas = [
    {
      clave: 'asistencia', titulo: 'Asistencia · 30 días',
      valor: `${miles(asis.diasConMarca30d)} día${asis.diasConMarca30d === 1 ? '' : 's'} con marca`,
      detalle: asis.ultimaMarca ? `Última marca: ${cuando(asis.ultimaMarca)}` : 'Sin marcas en 30 días',
      fuente: 'persona.asistencia', apagada: false, falta: '', abre: true
    },
    {
      clave: 'nexos', titulo: 'Nexos',
      valor: miles(nexos.saldo),
      detalle: `${miles(nexos.movimientos)} movimientos · la misma cifra en todo el panel`,
      fuente: nexos.fuente || 'persona.nexos', apagada: false, falta: ''
    }
  ];

  if (!danos.length) {
    tarjetas.push({ clave: 'danos', titulo: 'Daños en sus conteos', valor: '—', detalle: 'No tiene fichas de operación con conteo', fuente: 'persona.danos', apagada: true, falta: '' });
  } else {
    const pesos = danos.reduce((a, d) => a + d.pesos, 0);
    const n = danos.reduce((a, d) => a + d.danos, 0);
    const conteos = danos.reduce((a, d) => a + d.conteos, 0);
    tarjetas.push({
      // D-41 (nexo-api 305, servido): el dano se reparte entre quienes trabajaron ese dia en el area.
      clave: 'danos', titulo: 'Daños de su área · su parte del mes operativo',
      valor: pesosCOP(pesos),
      detalle: `su parte de ${miles(n)} daño${n === 1 ? '' : 's'} · ${miles(conteos)} conteo${conteos === 1 ? '' : 's'} que hizo`
        + (danos.length > 1 ? ' · ' + danos.map((d) => `${negocio(d.businessId)} ${pesosCOP(d.pesos)}`).join(' / ') : ''),
      fuente: 'persona.danos (repartido entre quienes trabajaron en el área)', apagada: false, falta: '', abre: true
    });
  }
  // SUJ-T8 bloque 2: los creditos se consultan al abrir la tarjeta (GET persona/creditos, nexo-api 308).
  tarjetas.push({
    clave: 'creditos', titulo: 'Créditos', valor: '—', detalle: 'Se consulta al abrir',
    fuente: 'persona/creditos (confirmados por documento o nombre; posibles por teléfono)', apagada: false, falta: '', abre: true
  });

  const panel = cuando(con.panel);
  const arcade = cuando(con.arcade);
  tarjetas.push({
    clave: 'conexion', titulo: 'Última conexión',
    valor: panel || '—',
    detalle: `Panel: ${panel || 'sin dato'} · Arcade: ${arcade || 'sin dato'}`,
    fuente: 'persona.conexiones', apagada: !panel && !arcade,
    falta: !panel && !arcade ? (con.fuentePanel || 'se guarda desde el 21-sep') : ''
  });
  return tarjetas;
}

/** Las fichas de operacion, con el nombre del negocio, para el detalle por negocio. */
export function fichasPorNegocio(p) {
  const negocio = (id) => (p?.membresias || []).find((m) => m.businessId === id)?.negocio || id;
  return (p?.llavesOperacion || [])
    .filter((o) => o.colaboradorId && o.businessId)
    .map((o) => ({ colaboradorId: o.colaboradorId, businessId: o.businessId, negocio: negocio(o.businessId), tienePin: o.tienePin }));
}
